import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Lightbulb, ChevronRight, Loader2, Mail, Check } from 'lucide-react';

interface Insight {
  id: string;
  title: string;
  content: string;
  category: string;
  relatedLesson: {
    id: string;
    slug: string;
    title: string;
    path: string;
  } | null;
}

interface InsightResponse {
  insight: Insight | null;
  date: string;
}

const categoryStyles: Record<string, { label: string; bgColor: string; textColor: string }> = {
  cognitive_bias: { label: 'Cognitive Bias', bgColor: 'bg-purple-500/20', textColor: 'text-purple-400' },
  critical_thinking: { label: 'Critical Thinking', bgColor: 'bg-blue-500/20', textColor: 'text-blue-400' },
  ai_literacy: { label: 'AI Literacy', bgColor: 'bg-green-500/20', textColor: 'text-green-400' },
  prompt_craft: { label: 'Prompt Craft', bgColor: 'bg-orange-500/20', textColor: 'text-orange-400' },
  attention: { label: 'Attention', bgColor: 'bg-cyan-500/20', textColor: 'text-cyan-400' },
  general: { label: 'Daily Thought', bgColor: 'bg-gray-500/20', textColor: 'text-gray-400' }
};

export default function DailyInsight() {
  const [insight, setInsight] = useState<Insight | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [subscribeStatus, setSubscribeStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [subscribeMessage, setSubscribeMessage] = useState('');

  useEffect(() => {
    loadInsight();
  }, []);

  async function loadInsight() {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL || 'http://127.0.0.1:8787'}/api/insights/today`
      );
      const data: InsightResponse = await response.json();
      setInsight(data.insight);
    } catch (err) {
      setError('Could not load today\'s insight');
      console.error('Failed to load insight:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleSubscribe(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;

    setSubscribeStatus('loading');
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL || 'http://127.0.0.1:8787'}/api/insights/subscribe`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: email.trim() })
        }
      );
      const data = await response.json();

      if (response.ok) {
        setSubscribeStatus('success');
        setSubscribeMessage(data.message || 'Subscribed! Check your inbox.');
        setEmail('');
      } else {
        setSubscribeStatus('error');
        setSubscribeMessage(data.error || 'Failed to subscribe');
      }
    } catch (err) {
      setSubscribeStatus('error');
      setSubscribeMessage('Failed to subscribe');
      console.error('Subscribe error:', err);
    }
  }

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-500/20 rounded-xl p-6 mb-6">
        <div className="flex items-center gap-3">
          <Loader2 className="w-5 h-5 text-amber-400 animate-spin" />
          <span className="text-gray-400">Loading today's insight...</span>
        </div>
      </div>
    );
  }

  if (error || !insight) {
    return null; // Fail silently - don't break the page
  }

  const category = categoryStyles[insight.category] || categoryStyles.general;

  return (
    <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-500/20 rounded-xl p-6 mb-6">
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
          <Lightbulb className="w-5 h-5 text-amber-400" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-2">
            <span className="text-xs font-medium text-amber-400 uppercase tracking-wide">
              Today's Insight
            </span>
            <span className={`text-xs px-2 py-0.5 rounded-full ${category.bgColor} ${category.textColor}`}>
              {category.label}
            </span>
          </div>

          <h2 className="text-lg font-semibold text-white mb-2">
            {insight.title}
          </h2>

          <p className="text-gray-300 leading-relaxed">
            {insight.content}
          </p>

          {insight.relatedLesson && (
            <Link
              to={`/lessons/${insight.relatedLesson.slug}`}
              className="inline-flex items-center gap-1 mt-4 text-sm text-amber-400 hover:text-amber-300 transition-colors group"
            >
              Learn more: {insight.relatedLesson.title}
              <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          )}

          {/* Email subscription form */}
          <div className="mt-5 pt-5 border-t border-amber-500/20">
            {subscribeStatus === 'success' ? (
              <div className="flex items-center gap-2 text-green-400 text-sm">
                <Check className="w-4 h-4" />
                {subscribeMessage}
              </div>
            ) : (
              <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-2">
                <div className="flex-1 relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Get daily insights via email"
                    className="w-full pl-10 pr-4 py-2 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none text-sm"
                    disabled={subscribeStatus === 'loading'}
                  />
                </div>
                <button
                  type="submit"
                  disabled={subscribeStatus === 'loading' || !email.trim()}
                  className="px-4 py-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 disabled:cursor-not-allowed text-gray-900 font-medium rounded-lg transition-colors text-sm whitespace-nowrap"
                >
                  {subscribeStatus === 'loading' ? 'Subscribing...' : 'Subscribe'}
                </button>
              </form>
            )}
            {subscribeStatus === 'error' && (
              <p className="mt-2 text-red-400 text-sm">{subscribeMessage}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
